import {gsap} from "gsap";
import {ContinueButton} from "@nolimitcity/slot-launcher/bin/gui/buttons/concretebuttons/ContinueButton";
import {Helper} from "../../../../core/utils/Helper";
import {PunkRocker} from "../../../PunkRocker";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {SplashViewFreeSpins} from "./SplashViewFreeSpins";
import {SoundManager} from "../../../sound/SoundManager";
import {PRGameMode} from "../../../server/PRGameMode";
import {SoundConfig} from "../../../sound/SoundConfig";
import {GameData} from "../../../server/data/GameData";
import {SpinePlayer} from "../../../../core/displayobject/SpinePlayer";
import {PunkSymbol} from "../../../punkEngine/symbol/PunkSymbol";
import {PREngineManager} from "../../../punkEngine/PREngineManager";

export class SplashController extends PIXI.Container {
    private _continueButton: ContinueButton;
    private _gfxBg: PIXI.Sprite;
    private _intro: SplashViewFreeSpins;
    private _playedIndex: number | undefined = 0;
    private _onClicked: Function | undefined;
    private _timeline: gsap.core.Timeline;
    private _loop: gsap.core.Timeline;
    private _scatterPos: number[] = [];
    private _isCapWin: boolean = false;
    private _isUpgrade: boolean = false;
    private _nextMode: string = "";
    private _gameData: GameData;

    constructor() {
        super();
        this.name = "SplashController";

        this.init();
        this.onResize();
    }

    public get button(): ContinueButton {
        return this._continueButton;
    }

    public setBGAlpha(alpha: number, durr: number): gsap.core.Timeline {
        const tl = gsap.timeline();
        tl.add(() => {
            this.visible = true;
            this.button.alpha = 0;
            this._gfxBg.visible = false;
            PunkRocker.gameLogo.setBGAlpha(0, alpha, durr);
        });
        tl.fromTo(this._gfxBg, {alpha: 0}, {
            alpha: alpha,
            duration: durr
        });
        return tl;
    }

    public setData(data: any): void {
        this._gameData = data;
        this._scatterPos = data.Reel.scatterPositions;
        this._scatterPos = data.Reel.scatterPositions;
        this._nextMode = data.raw.nextMode;
        this._isUpgrade = (data.mode === PRGameMode.FREESPIN_LEVEL_ONE
                && (data.nextMode === PRGameMode.FREESPIN_LEVEL_TWO || data.nextMode === PRGameMode.FREESPIN_LEVEL_THREE))
            || (data.mode === PRGameMode.FREESPIN_LEVEL_TWO && data.nextMode === PRGameMode.FREESPIN_LEVEL_THREE);
        this._intro.setData(data);
    }

    public showFSIntro(onClicked?: Function): gsap.core.Timeline {
        this.onResize();

        this._onClicked = onClicked;

        this._timeline = Helper.setTimeline(this._timeline, new gsap.core.Timeline({
            onStart: () => {
                this.visible = true;
                this.alpha = 1
            }
        }));
        this._timeline.add([
            () => {
                SoundManager.playScreenMusic(SoundConfig.FS_INTRO, 0.01);
                SoundManager.playSoundEffect(`fsIntroSplashLvl${(this._nextMode === PRGameMode.FREESPIN_LEVEL_ONE ? 1 : (this._nextMode === PRGameMode.FREESPIN_LEVEL_TWO ? 2 : 3))}`)
            },
            this.setBGAlpha(0.5, 0.5)
        ], 0)

        if (this._playedIndex != undefined) {
            this._timeline.add(this._intro.play());
        }

        if (this._isUpgrade) {
            this._timeline.add(() => {
                this._continueButton.enable(true);
                this._continueButton.alpha = 0;
                this._continueButton.visible = false;
            }, "-=0.25");
            this._timeline.to(this, {
                duration: 3,
                onStart: () => {
                    PunkRocker.noService.arriveBottomTrainOnly("UPGRADE");
                },
                onComplete: () => {
                    this.clickCallback();
                }
            });
        } else {
            // #339
            if (this._gameData.raw.wasFeatureBuy && PunkRocker.autoPlayPlugin.isAutoplayRound) {
                this._timeline.to(this, {
                    duration: 1
                });
                this._timeline.add(() => {
                    this.clickCallback();
                });
            } else {
                this._timeline.add([
                    () => {
                        this._continueButton.visible = true;
                    },
                    gsap.fromTo(this._continueButton, {duration: 0.25, alpha: 0}, {alpha: 1})
                ], "-=0.25");
                this._timeline.add(() => {
                    this._continueButton.enable(true);
                });
            }
        }

        this._loop = Helper.setTimeline(this._loop, new gsap.core.Timeline({repeat: -1}));
        this._loop.to(this, {duration: 100});

        return this._loop;
    }

    public stop(): void {
        this.clickCallback()
    }

    public onResize(): void {
        if (NolimitApplication.isLandscape) {
            this._continueButton.position.set(360, 615);
        } else {
            const centerBottom: number = (NolimitApplication.screenBounds.bottom - 610) * 0.5 + 610;
            this._continueButton.position.set(360, centerBottom - 80);
        }
    }

    private init(): void {
        this._gfxBg = new PIXI.Sprite();
        this.createPages();
        this.createInteractiveModules();

        this.sortChildren();

        this.visible = false;
    }

    private createPages(): void {
        this._intro = new SplashViewFreeSpins();
        this.addChild(this._intro);
    }

    private createInteractiveModules(): void {
        this._continueButton = new ContinueButton();
        this._continueButton.addClickCallback(() => this.clickCallback());
        this._continueButton.pivot.set(this._continueButton.width * 0.5, this._continueButton.height * 0.5);
        this._continueButton.enable(false);
        this._continueButton.alpha = 0;
        this.addChild(this._continueButton);
    }

    private clickCallback(): void {
        this._isUpgrade = false;
        PunkRocker.jumpWilds.onFSIntroContinueClick();
        PunkRocker.keypadPlugin.setZeroBetSpinCounter(this._gameData.raw.freespinsLeft);
        PunkRocker.gameLogo.setBGAlpha(0.5, 0, 0.5);
        this._timeline = Helper.setTimeline(this._timeline, new gsap.core.Timeline({
            onComplete: () => {
                Helper.setTimeline(this._loop);
                this.alpha = 1;
                this.visible = false;
            }
        }));

        let landedScatterCount: number = 0,
            tlDelay: number = 0.1;

        PunkRocker.reelEngineManager.AllReels.forEach((r, rid) => {
            r.AllWindowSymbols.forEach(s => {
                if (s.isScatter) {
                    landedScatterCount++;

                    if (landedScatterCount < 5) {
                        this._timeline.add([
                            s.showPoppedSkullOnScatters(),
                            () => {
                                // #381
                                SoundManager.playSoundEffect(SoundConfig.FS_START_SCATTER);
                            }
                        ], tlDelay);
                        tlDelay += 0.1;
                    }

                    if (landedScatterCount === 5) {
                        const skullPopDur: number = SpinePlayer.fetchDuration("scatterSymbol", "allSkullPop");

                        tlDelay += skullPopDur + 0.1;
                        this._timeline.add([
                            s.burnTrainFromScatter(),
                            () => {
                                // #381
                                SoundManager.playSoundEffect(SoundConfig.FS_START_SCATTER_5_EXPLODE);
                            }
                        ], tlDelay);

                        tlDelay += 0.1;
                        this._timeline.add([
                            PunkRocker.otherOverlays.playTrainBlast(),
                            () => {
                                // #381
                                SoundManager.playSoundEffect(SoundConfig.FS_START_TRAIN_EXPLODE);
                            }
                        ], tlDelay);

                        tlDelay += 0.2;
                        this._timeline.add(PunkRocker.trainsManager.bottomTrain.performTrainBlast(), tlDelay);
                    }
                }
            });
        });

        const reelsWithScatter: (number | null)[] = this._scatterPos.map((num, index) => num > -1 ? index : null).filter(index => index !== null);

        for (let jwId: number = 0; jwId < reelsWithScatter.length; jwId++) {
            const reelId: number | null = reelsWithScatter[jwId];

            if(reelId !== null && jwId < 4) {
                this._timeline.add(()=>{
                    const scatterPosId: number = this._scatterPos[reelId],
                        scatterSym: PunkSymbol | undefined = PREngineManager.AllReels[reelId].getSymbol(scatterPosId);

                    if(scatterSym) {
                        scatterSym.onSkullPopped();
                    }
                    PunkRocker.jumpWilds.startShowJW(jwId, reelId, scatterPosId);
                });
            }
        }

        if (this._onClicked) {
            this._onClicked();
        }

        PunkRocker.trainsManager.topTrain.showGlowPositions([0, 1, 2, 3, 4, 5], false, 0.5);
        PunkRocker.trainsManager.bottomTrain.showGlowPositions([0, 1, 2, 3, 4, 5], false, 0.5);

        if (this._isCapWin) {
            return;
        }
        if (this._playedIndex != undefined) this._timeline.add(this._intro.stop(), 0);
        const stopDurr: number = this._timeline.duration();
        this._timeline.fromTo(this, {duration: stopDurr ? stopDurr : 0.25, alpha: 1}, {alpha: 0}, 0);
        this._continueButton.enable(false);
    }
}
